import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Download, Share, Plus, X } from "lucide-react";

const DISMISS_KEY = "ss_pwa_install_dismissed";

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isIPad = /iPad/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return /iPhone|iPod/.test(ua) || isIPad;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPWA() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    // If user dismissed within the last 7 days, don't nag.
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    if (dismissedAt && Date.now() - dismissedAt < sevenDays) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari does not fire beforeinstallprompt.
    if (isIOS() && !isStandalone()) {
      setVisible(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIosSheet(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") setInstalled(true);
    } catch {
      /* noop */
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (installed || !visible) return null;

  return (
    <>
      {/* Floating install button (mobile bottom, desktop bottom-right) */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 z-40 flex items-center gap-2 shadow-2xl rounded-full bg-primary text-primary-foreground pl-2 pr-1 py-1 animate-in fade-in slide-in-from-bottom-4"
        data-testid="install-pwa-container"
      >
        <button
          onClick={handleInstall}
          className="flex items-center gap-2 pl-3 pr-2 py-2 text-sm font-medium hover:opacity-90"
          data-testid="install-pwa-btn"
          aria-label={t("pwa.install_aria")}
        >
          <Download className="w-4 h-4" aria-hidden="true" />
          <span>{t("pwa.install_cta")}</span>
        </button>
        <button
          onClick={handleDismiss}
          className="p-2 rounded-full hover:bg-white/15"
          data-testid="install-pwa-dismiss"
          aria-label={t("pwa.dismiss_aria")}
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      {/* iOS instructions dialog */}
      <Dialog open={showIosSheet} onOpenChange={setShowIosSheet}>
        <DialogContent data-testid="install-pwa-ios-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" aria-hidden="true" />
              {t("pwa.ios_title")}
            </DialogTitle>
            <DialogDescription>{t("pwa.ios_intro")}</DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary font-semibold shrink-0">1</span>
              <span className="pt-0.5 flex items-center gap-2">
                {t("pwa.ios_step1_before")}
                <Share className="w-4 h-4 inline text-primary" aria-hidden="true" />
                {t("pwa.ios_step1_after")}
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary font-semibold shrink-0">2</span>
              <span className="pt-0.5 flex items-center gap-2">
                {t("pwa.ios_step2_before")}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-xs font-medium">
                  <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                  {t("pwa.ios_step2_label")}
                </span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary font-semibold shrink-0">3</span>
              <span className="pt-0.5">{t("pwa.ios_step3")}</span>
            </li>
          </ol>
          <DialogFooter>
            <Button onClick={() => setShowIosSheet(false)} data-testid="install-pwa-ios-close" className="rounded-full">
              {t("pwa.ios_got_it")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
